/**
 * Shared Quora profile configuration (onboarding + Settings).
 */
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { channelsApi } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { quoraConfigurePropType } from "@/lib/propTypes/connections";

const QUORA_PROFILE_RE = /^https?:\/\/(www\.)?quora\.com\/profile\/[\w\-.]+\/?$/i;

function normalizeProfileUrl(raw) {
  const v = (raw || "").trim();
  if (!v) return "";
  if (v.startsWith("http")) return v;
  if (v.startsWith("quora.com/")) return `https://www.${v}`;
  if (v.startsWith("www.quora.com/")) return `https://${v}`;
  return v;
}

export function useQuoraConfigure({ connectionId, ensureConnection, canEdit, onConfigured }) {
  const [profileUrl, setProfileUrl] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [connectBusy, setConnectBusy] = useState(false);
  const [error, setError] = useState(null);

  const profileValid = QUORA_PROFILE_RE.test(normalizeProfileUrl(profileUrl));
  const nameValid = (displayName || "").trim().length >= 1;

  const resolveConnId = useCallback(async () => {
    if (connectionId) return connectionId;
    if (!ensureConnection) return null;
    const row = await ensureConnection("quora");
    return row?.id || null;
  }, [connectionId, ensureConnection]);

  const connect = useCallback(async () => {
    if (!canEdit) {
      toast.error("Only workspace admins can connect Quora");
      return;
    }
    const url = normalizeProfileUrl(profileUrl);
    const name = displayName.trim();
    if (!profileValid) {
      setError("Profile URL must look like https://www.quora.com/profile/Your-Name");
      return;
    }
    if (!nameValid) {
      setError("Enter your display name as shown on Quora");
      return;
    }
    setConnectBusy(true);
    setError(null);
    try {
      const connId = await resolveConnId();
      if (!connId) {
        setError("Connection row missing — refresh and try again");
        return;
      }
      const r = await channelsApi.configureQuora(connId, {
        profile_url: url,
        display_name: name,
      });
      toast.success("Quora connected");
      await onConfigured?.(r);
    } catch (e) {
      const msg = e?.message || "Couldn't connect Quora";
      setError(msg);
      toast.error(msg);
    } finally {
      setConnectBusy(false);
    }
  }, [
    canEdit, profileUrl, displayName, profileValid, nameValid,
    resolveConnId, onConfigured,
  ]);

  return {
    profileUrl,
    setProfileUrl,
    displayName,
    setDisplayName,
    connectBusy,
    error,
    profileValid,
    nameValid,
    connect,
  };
}

export function QuoraConnectForm({ quora, canEdit, testIdPrefix = "quora" }) {
  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-connect-form`}>
      <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 text-[11.5px] text-slate-600 space-y-1">
        <div>1. Open your Quora profile in a browser and copy the URL.</div>
        <div>2. Paste it below with your display name.</div>
        <div className="text-slate-500">Browser session sync via OpenClaw lands in a later release.</div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-profile`} className="text-[11.5px]">Quora profile URL</Label>
        <Input
          id={`${testIdPrefix}-profile`}
          placeholder="https://www.quora.com/profile/Jane-Doe"
          value={quora.profileUrl}
          onChange={(e) => quora.setProfileUrl(e.target.value)}
          disabled={!canEdit || quora.connectBusy}
          data-testid={`${testIdPrefix}-profile-input`}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-name`} className="text-[11.5px]">Display name</Label>
        <Input
          id={`${testIdPrefix}-name`}
          placeholder="Jane Doe"
          value={quora.displayName}
          onChange={(e) => quora.setDisplayName(e.target.value)}
          disabled={!canEdit || quora.connectBusy}
          data-testid={`${testIdPrefix}-name-input`}
        />
      </div>
      {quora.error && (
        <div className="text-[11px] text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-md px-3 py-2">
          {quora.error}
        </div>
      )}
      <Button
        className="w-full"
        disabled={!canEdit || quora.connectBusy || !quora.profileValid || !quora.nameValid}
        onClick={quora.connect}
        data-testid={`${testIdPrefix}-connect-btn`}
      >
        {quora.connectBusy ? <><Loader2 size={14} className="animate-spin mr-1" /> Connecting…</> : "Connect Quora"}
      </Button>
    </div>
  );
}

QuoraConnectForm.propTypes = {
  quora: quoraConfigurePropType.isRequired,
  canEdit: PropTypes.bool,
  testIdPrefix: PropTypes.string,
};
